import * as React from 'react';

import type { NativeWidgetConfigBase, NativeWidgetRenderer } from './types';

export const NATIVE_WIDGET_TYPES = {
  FLASHCARDS: 'flashcards',
} as const;

export type NativeWidgetType = (typeof NATIVE_WIDGET_TYPES)[keyof typeof NATIVE_WIDGET_TYPES];

/**
 * Registers a native widget renderer, passing the config type related to the renderer to the generic type parameter.
 * */
const registerNativeWidget = <T extends NativeWidgetConfigBase>(
  renderer: NativeWidgetRenderer<T>
): NativeWidgetRenderer => renderer as NativeWidgetRenderer;

/**
 * Maps a plugin `config.widgetRenderConfigurationType` to the React component that renders it natively.
 * Renderers are lazy-loaded so requiring the registry doesn't eagerly pull in every plugin's dependency tree.
 */
export const NATIVE_WIDGET_REGISTRY: Record<NativeWidgetType, NativeWidgetRenderer> = {
  [NATIVE_WIDGET_TYPES.FLASHCARDS]: registerNativeWidget(
    React.lazy(() =>
      import('bundles/native-widgets/flashcards/FlashcardsPlugin').then((module) => ({
        default: module.FlashcardsPlugin,
      }))
    )
  ),
};

/**
 * Returns the native renderer for a plugin `widgetRenderConfigurationType`, or `undefined` if the
 * type is absent/unknown — in which case the caller should fall back to the iframe renderer.
 */
export const getNativeWidgetRenderer = (type: string | undefined): NativeWidgetRenderer | undefined =>
  type ? NATIVE_WIDGET_REGISTRY[type as NativeWidgetType] : undefined;
